import { useCallback, useEffect, useRef, useState } from 'react'
import './App.css'
import { useMsal } from '@azure/msal-react'
import { apiSearchScopeAction } from './authConfig'
import Brightness4Icon from '@mui/icons-material/Brightness4'
import useCustomerConfig from './hooks/useCustomerConfig'
import { Chat } from './components/Chat'
import { FeedbackExplorer } from './components/FeedbackExplorer'
import { Message } from './types'
import { ChatInputBox } from './components/ChatInputBox'
import { GiveFeedback } from './components/GiveFeedback'
import { Button } from './components/Buttons'
import MenuIcon from '@mui/icons-material/Menu'
import MapsUgcIcon from '@mui/icons-material/MapsUgc'
import {
  createNewChatbotThread,
  getMe,
  getChatbotThreadModel,
  Me,
  postChatMsgToThreadAndStreamResponse,
  fillQuestionnaire,
  submitQuestionnaire,
  getQuestionnaireFields,
} from './chatApi'

import { SystemPromptSelector } from './components/SystemPromptSelector'
import { Sidebar, Tab } from './components/Sidebar'
import { Questionnaire } from './components/Questionnaire'
import { QuestionnaireV2 } from './features/questionnaire/Questionnaire'
import ChatIcon from '@mui/icons-material/Chat'
import BarChartIcon from '@mui/icons-material/BarChart'
import { ButtonGroup } from './components/ButtonGroup'
import { UserIconAndName } from './components/UserIconAndName'
import { t } from './i18n'
import { ChatApiError } from './apiErrors'
import { ModelSelector } from './features/model-testing/ModelSelector'
import { ModelSelection } from './features/model-testing/types'
import { useModelCatalog } from './features/model-testing/useModelCatalog'
import { modelTestCopy, modelTestError } from './features/model-testing/copy'

const getAccessTokenForSemanticApi = async (accounts: any, instance: any) => {
  const accessTokenRequest = {
    scopes: [apiSearchScopeAction],
    account: accounts[0],
  }
  try {
    const accessTokenResponse =
      await instance.acquireTokenSilent(accessTokenRequest)
    return accessTokenResponse.accessToken
  } catch (error) {
    console.log('Silent token acquisition fails. Acquiring token using popup')
    try {
      const accessTokenResponse =
        await instance.acquireTokenRedirect(accessTokenRequest)
      return accessTokenResponse.accessToken
    } catch (err) {
      console.error(err)
      return null
    }
  }
}

const availableTabs: Tab[] = [
  { id: 'chat', label: t('chat'), icon: <ChatIcon /> },
  { id: 'statistikk', label: t('statistics'), icon: <BarChartIcon /> },
]

const App = () => {
  const { instance, accounts } = useMsal()
  const [darkMode, setDarkMode] = useState(() => {
    const storedMode = localStorage.getItem('darkMode')
    if (storedMode !== null) {
      return storedMode === 'true'
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  })
  const customerConfig = useCustomerConfig()
  const usesQuestionnaireV2 = customerConfig.configName === 'sexogsamfunn'
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [threadId, setThreadId] = useState<string | null>(null)
  const [conversation, setConversation] = useState<Message[]>([])
  const [botIsTyping, setBotIsTyping] = useState(false)
  const [activeTab, setActiveTab] = useState<string>('chat')
  const [customSystemPrompt, setCustomSystemPrompt] = useState<string | null>(
    null,
  )
  const [selectedSystemPromptId, setSelectedSystemPromptId] = useState<
    string | null
  >(null)
  const [me, setMe] = useState<Me | null>(null)
  const accountId = accounts[0]?.homeAccountId
  const [meAccountId, setMeAccountId] = useState<string | undefined>()
  const meRequest = useRef(0)
  const isAdmin =
    accounts.length > 0 && meAccountId === accountId && me?.isAdmin === true
  const [modelSelection, setModelSelection] = useState<ModelSelection | null>(
    null,
  )
  const [threadError, setThreadError] = useState<string | null>(null)
  const [creatingThread, setCreatingThread] = useState(false)
  // A synchronous lock also covers the wait for an access token and double clicks.
  const operation = useRef<'thread' | 'message' | null>(null)
  const getToken = useCallback(
    () => getAccessTokenForSemanticApi(accounts, instance),
    [accounts, instance],
  )
  const models = useModelCatalog(isAdmin, customerConfig.chatServer, getToken)
  const canSelectModel = isAdmin && models.catalog !== null
  const [questionnaireFields, setQuestionnaireFields] = useState<any[] | null>(
    null,
  )

  const startNewConversationThread = async (
    alias: string | null = modelSelection?.modelAlias ?? null,
  ): Promise<boolean> => {
    if (operation.current) return false
    operation.current = 'thread'
    setCreatingThread(true)
    setThreadError(null)
    try {
      const token = await getToken()
      if (!token) throw new ChatApiError(401, 'No access token')
      const thread = await createNewChatbotThread(
        token,
        customerConfig.chatServer,
        canSelectModel ? alias : undefined,
      )
      const selection = canSelectModel
        ? await getChatbotThreadModel(
            token,
            customerConfig.chatServer,
            thread.id,
          )
        : null
      setThreadId(thread.id)
      setModelSelection(selection)
      setConversation([])
      return true
    } catch (error) {
      setThreadError(modelTestError(error))
      console.error('Failed to start conversation', error)
      return false
    } finally {
      operation.current = null
      setCreatingThread(false)
    }
  }

  // The initial ordinary thread may exist before the model catalog is loaded.
  useEffect(() => {
    if (creatingThread || !canSelectModel || !threadId || modelSelection) return
    let current = true
    const confirmModel = async () => {
      try {
        const token = await getToken()
        if (!token) throw new ChatApiError(401, 'No access token')
        const selection = await getChatbotThreadModel(
          token,
          customerConfig.chatServer,
          threadId,
        )
        if (current) setModelSelection(selection)
      } catch (error) {
        if (current) {
          setThreadError(
            modelTestError(error, modelTestCopy.confirmationFailed),
          )
        }
      }
    }
    void confirmModel()
    return () => {
      current = false
    }
  }, [
    creatingThread,
    canSelectModel,
    threadId,
    modelSelection,
    getToken,
    customerConfig.chatServer,
  ])

  const fetchMe = async () => {
    const request = ++meRequest.current
    try {
      const token = await getToken()
      if (!token) return
      const me = await getMe(token, customerConfig.chatServer)
      if (request !== meRequest.current) return
      setMeAccountId(accountId)
      setMe(me)
      if (me) {
        if (
          customSystemPrompt === null &&
          selectedSystemPromptId === null &&
          me.alternativeSystemPrompts?.length
        ) {
          setSelectedSystemPromptId(me.alternativeSystemPrompts[0].name)
        }
      }
    } catch (error) {
      console.error('Failed to load chatbot permissions', error)
    }
  }

  const getQuestionnaireFieldsX = async () => {
    const token = await getAccessTokenForSemanticApi(accounts, instance)
    if (!token) {
      console.error('Failed to get access token')
      return
    }
    const fields = await getQuestionnaireFields(
      token,
      customerConfig.chatServer,
    )
    setQuestionnaireFields(fields)
  }

  useEffect(() => {
    if (
      activeTab === 'statistikk' &&
      !usesQuestionnaireV2 &&
      questionnaireFields === null
    ) {
      getQuestionnaireFieldsX()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  useEffect(() => {
    if (accounts.length > 0 && (!me || meAccountId !== accountId)) {
      void fetchMe()
    }
    if (accounts.length > 0 && instance && threadId === null) {
      const timer = setTimeout(() => {
        void startNewConversationThread(null)
      }, 250)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, instance, threadId])

  const toggleDarkMode = () => {
    setDarkMode((prevMode) => {
      const newMode = !prevMode
      localStorage.setItem('darkMode', newMode.toString())
      return newMode
    })
  }

  const onFeedbackSubmit = async (grade: number, comment: string) => {
    const token = await getAccessTokenForSemanticApi(accounts, instance)
    if (!token) {
      console.error('Failed to get access token')
      return
    } else {
      await fetch(customerConfig.chatServer + '/feedback', {
        method: 'POST',
        body: JSON.stringify({ grade, comment, conversation }),
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
      })
    }
  }

  const onSubmitChatMessage = async (messageText: string) => {
    if (operation.current || !threadId) return
    operation.current = 'message'
    setBotIsTyping(true)
    setThreadError(null)
    const newConversation = [
      ...conversation,
      { role: 'user', text: messageText } as Message,
    ]
    setConversation(newConversation)
    const updateAssistantAnswer = (updateFn: (msg: Message) => Message) => {
      setConversation((conversation) => {
        const last = conversation[conversation.length - 1]
        if (last?.role === 'assistant') {
          return [...conversation.slice(0, -1), updateFn(last)]
        } else {
          const newMessage: Message = {
            role: 'assistant',
            text: '',
            steps: [],
            documents: [],
            citations: [],
          }
          return [...conversation, updateFn(newMessage)]
        }
      })
    }
    try {
      const token = await getToken()
      if (!token) throw new ChatApiError(401, 'No access token')
      const responseStream = postChatMsgToThreadAndStreamResponse(
        token,
        customerConfig.chatServer,
        threadId,
        messageText,
        selectedSystemPromptId,
        customSystemPrompt,
      )
      for await (const chunk of responseStream) {
        if (isAdmin && chunk.type === 'error') {
          updateAssistantAnswer((last) => ({
            ...last,
            responseError: chunk.message || modelTestCopy.failed,
          }))
          break
        }
        if (chunk.answer) {
          updateAssistantAnswer((last) => ({
            ...last,
            text: last.text + chunk.answer,
          }))
        }
        if (chunk.documents) {
          updateAssistantAnswer((last) => ({
            ...last,
            // Find the step with the same runId, and append documents to it:
            steps: (last.steps || []).map((step) => {
              if (step.runId === chunk.runId) {
                return { ...step, documents: chunk.documents }
              } else {
                return step
              }
            }),
            documents: [...(last.documents || []), ...chunk.documents],
          }))
        }
        if (chunk.citations) {
          updateAssistantAnswer((last) => ({
            ...last,
            citations: [...(last.citations || []), ...chunk.citations],
          }))
        }
        if (chunk.facts) {
          updateAssistantAnswer((last) => ({
            ...last,
            facts: [...(last.facts || []), ...chunk.facts],
          }))
        }
        if (chunk.removedDocumentIds) {
          updateAssistantAnswer((last) => ({
            ...last,
            documents: (last.documents || []).filter(
              (doc) => !chunk.removedDocumentIds.includes(doc.id),
            ),
          }))
        }
        if (chunk.steps) {
          updateAssistantAnswer((last) => ({
            ...last,
            steps: [...(last.steps || []), ...chunk.steps],
          }))
        }
      }
    } catch (error) {
      if (isAdmin) {
        updateAssistantAnswer((last) => ({
          ...last,
          responseError: modelTestError(error, modelTestCopy.connectionFailed),
        }))
      }
      console.error('Failed to receive answer', error)
    } finally {
      operation.current = null
      setBotIsTyping(false)
    }
  }

  const fillQuestionnaireX = async (text: string) => {
    const token = await getAccessTokenForSemanticApi(accounts, instance)
    if (!token) {
      console.error('Failed to get access token')
      return ''
    }
    const response = await fillQuestionnaire(
      token,
      customerConfig.chatServer,
      text,
    )
    return response
  }

  const submitQuestionnaireX = async (answers: any) => {
    const token = await getAccessTokenForSemanticApi(accounts, instance)
    if (!token) {
      console.error('Failed to get access token')
      return
    }
    const response = await submitQuestionnaire(
      token,
      customerConfig.chatServer,
      answers,
    )
    return response
  }

  const conversationEmpty = conversation.length === 0
  const waitingForBotToRespond =
    conversation.length > 0 &&
    conversation[conversation.length - 1].role === 'user'

  return (
    <div
      className={`h-screen ${darkMode ? 'dark' : ''} flex ${customerConfig.theme.isLight ? 'bg-white dark:bg-gray-900 dark:text-white' : customerConfig.theme.bgColor}`}
    >
      <Sidebar
        darkMode={darkMode}
        activeTab={activeTab}
        availableTabs={customerConfig.tabsInSidebar ? availableTabs : []}
        setActiveTab={setActiveTab}
        isOpen={sidebarOpen}
        extraChildren={
          <div className={'flex flex-col justify-center gap-3 p-3'}>
            {accounts && accounts.length > 0 && (
              <>
                {me && me.alternativeSystemPrompts.length >= 2 && (
                  <ButtonGroup header={t('aiPersonality')}>
                    <SystemPromptSelector
                      canUseCustomSystemPrompt={me.canUseCustomSystemPrompt}
                      customSystemPrompt={customSystemPrompt}
                      setCustomSystemPrompt={setCustomSystemPrompt}
                      selectedSystemPromptId={selectedSystemPromptId}
                      setSelectedSystemPromptId={setSelectedSystemPromptId}
                      alternativeSystemPrompts={me.alternativeSystemPrompts}
                    />
                  </ButtonGroup>
                )}
                <ButtonGroup>
                  {conversation.length > 0 && (
                    <GiveFeedback onSubmit={onFeedbackSubmit} />
                  )}
                  <FeedbackExplorer
                    getToken={() =>
                      getAccessTokenForSemanticApi(accounts, instance)
                    }
                  />
                  <Button onClick={toggleDarkMode} variant="flat">
                    <Brightness4Icon />
                    {t('darkMode')}
                  </Button>
                </ButtonGroup>
              </>
            )}
            <ButtonGroup>
              <UserIconAndName />
            </ButtonGroup>
            {customerConfig.version && (
              <span className={`text-center text-gray-600 text-xs`}>
                {customerConfig.version}
              </span>
            )}
          </div>
        }
      />
      <div
        className={
          'flex flex-col  ' +
          // (customerConfig.theme.isLight ? 'bg-white' : customerConfig.theme.bgColor) +
          ' ' +
          (sidebarOpen ? 'flex-1' : 'flex-1')
        }
      >
        <nav
          className={`flex justify-between items-center p-3 ${customerConfig.theme.isLight ? 'text-blue-600 dark:text-blue-300' : 'text-white'}`}
        >
          <div>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={'cursor-pointer lg:hidden'}
            >
              <MenuIcon />
            </button>
          </div>
          {/*<img src={customerConfig.logo} className="h-14 mb-4" alt="logo" />*/}
          <img
            src={
              customerConfig.theme.isLight && !darkMode
                ? 'semantic_lab_logo_farget.png'
                : 'semantic_lab_logo_hvit.png'
            }
            className="h-8"
            alt="logo"
          />
          <Button
            onClick={() => {
              void startNewConversationThread()
            }}
            variant="primary"
            disabled={isAdmin && (botIsTyping || creatingThread)}
          >
            <div className={'flex gap-1 items-center'}>
              <MapsUgcIcon />
              <span className={'hidden lg:inline'}>{t('newConversation')}</span>
            </div>
          </Button>
        </nav>
        {isAdmin &&
          activeTab === 'chat' &&
          !canSelectModel &&
          (models.error || models.unavailableStatus === 404 || threadError) && (
            <div className="model-testing-bar" data-dark={darkMode}>
              {models.unavailableStatus === 404 && (
                <p role="status">{modelTestCopy.backendUnavailable}</p>
              )}
              {models.error && (
                <>
                  <p className="model-testing-error" role="alert">
                    {models.error}
                  </p>
                  <button
                    type="button"
                    className="model-picker-retry"
                    onClick={models.reload}
                  >
                    {modelTestCopy.retry}
                  </button>
                </>
              )}
              {threadError && (
                <p className="model-testing-error" role="alert">
                  {threadError}
                </p>
              )}
            </div>
          )}
        {isAdmin && models.catalog && activeTab === 'chat' && (
          <ModelSelector
            selection={modelSelection}
            catalog={models.catalog}
            threadError={threadError}
            busy={botIsTyping || creatingThread}
            darkMode={darkMode}
            onStart={startNewConversationThread}
          />
        )}
        {activeTab === 'statistikk' && usesQuestionnaireV2 ? (
          <QuestionnaireV2
            key={`${customerConfig.chatServer}:${accountId}`}
            chatServer={customerConfig.chatServer}
            getToken={getToken}
          />
        ) : activeTab === 'statistikk' && questionnaireFields ? (
          <Questionnaire
            fields={questionnaireFields}
            fillQuestionnaire={fillQuestionnaireX}
            submitQuestionnaire={submitQuestionnaireX}
          />
        ) : (
          <Chat
            conversation={conversation}
            showErrors={isAdmin}
            emptyConversationPlaceholder={
              <div
                className={
                  'flex-1 flex gap-4 flex-col items-center text-xl text-gray-500'
                }
              >
                <h1
                  className={`text-4xl ${customerConfig.theme.textColor} font-bold dark:text-white`}
                >
                  {t('welcome')}
                </h1>
                <p className={'text-lg'}>{t('startConversation')}</p>
              </div>
            }
          />
        )}

        {activeTab === 'chat' && (
          <div className="p-3 bg-gray-100 dark:bg-gray-800">
            <ChatInputBox
              onSubmit={onSubmitChatMessage}
              quickTextActions={
                conversationEmpty ? [] : customerConfig.quickTextActions
              }
              canSubmit={
                !waitingForBotToRespond &&
                !botIsTyping &&
                !creatingThread &&
                threadId !== null
              }
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default App
